import { NumberInput, Title } from "@mantine/core";
import { type DateTime } from "luxon";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  useMantineReactTable
} from "mantine-react-table-open";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DateInput } from "../../../mantine/dates/DateInput/DateInput";
import { getDefaultMRTOptions } from "../../../mantine/getDefaultMRTOptions";
import { getDefaultMRTRowEditModeOptions } from "../../../mantine/getDefaultMRTRowEditModeOptions";
import type { DateRangeConsumptionShare } from "../../../utils/backend-types";
import { backendDateOrDateTimeToLuxonDateTimeOrNull } from "../../../utils/dates/backendDateOrDateTimeToLuxonDateTimeOrNull";
import { DATE_DISPLAY_FORMAT } from "../../../utils/dates/dates.constants";
import { luxonDateTimeToBackendDateOrDateTimeOrNull } from "../../../utils/dates/luxonDateTimeToBackendDateOrDateTimeOrNull";
import "./ConsumptionShareTable.scss";

interface ConsumptionShareTableProps {
  initialData: Array<DateRangeConsumptionShare>;
  onChangeConsumptionShareData: (
    index: number,
    consumerId?: number | null,
    percentile?: string | null,
    firstDate?: string | null,
    lastDate?: string | null
  ) => void;
  onClickDeleteRow(index: number): void;
  consumers: Array<{ consumer: number; consumerName: string }>;
}

interface ConsumptionShareData {
  [key: number]: string;
  firstDate: string | null;
  lastDate: string | null;
}

function ConsumptionShareTable({
  initialData,
  onChangeConsumptionShareData,
  onClickDeleteRow,
  consumers
}: ConsumptionShareTableProps) {
  const [consumptionShareData, setConsumptionShareData] = useState<
    Array<ConsumptionShareData>
  >([]);

  const handleChangeUserInput = useCallback(
    (
      index: number,
      consumerId?: number | null,
      percentile?: string | null,
      firstDate?: string | null,
      lastDate?: string | null
    ) => {
      onChangeConsumptionShareData(
        index,
        consumerId ?? null,
        percentile ? percentile : null,
        firstDate ?? initialData[index].firstDate ?? null,
        lastDate ?? initialData[index].lastDate ?? null
      );
    },
    [initialData, onChangeConsumptionShareData]
  );

  useEffect(() => {
    const newConsumptionShareData: Array<ConsumptionShareData> = [];
    if (initialData) {
      initialData.forEach((dateRangeShares) => {
        const consumptionShareDataObject: ConsumptionShareData = {
          firstDate: dateRangeShares.firstDate ?? null,
          lastDate: dateRangeShares.lastDate ?? null
        };
        dateRangeShares.consumerConsumptionShares.forEach((consumer) => {
          if (consumer.consumptionPercentile) {
            consumptionShareDataObject[consumer.consumer] =
              consumer.consumptionPercentile;
          }
        });

        newConsumptionShareData.push(consumptionShareDataObject);
      });
    }
    setConsumptionShareData(newConsumptionShareData);
  }, [initialData]);

  const mrtColumns: Array<MRT_ColumnDef<ConsumptionShareData>> = useMemo(
    () => [
      {
        header: "Gültig von",
        id: "firstDate",
        accessorFn: (rowData) =>
          backendDateOrDateTimeToLuxonDateTimeOrNull(rowData.firstDate),
        Cell: ({ cell }) =>
          cell.getValue<DateTime | null>()?.toFormat(DATE_DISPLAY_FORMAT) ??
          "/",
        minSize: 150,
        enableEditing: (row) => row.getValue("firstDate") !== null,
        Edit: ({ cell, row }) => {
          const date = cell.getValue<DateTime | null>();
          return (
            <DateInput
              id={`${row.index.toString()}-firstDate`}
              value={date}
              onChange={(value) => {
                handleChangeUserInput(
                  row.index,
                  null,
                  null,
                  luxonDateTimeToBackendDateOrDateTimeOrNull(value),
                  null
                );
              }}
            />
          );
        }
      },
      {
        header: "Gültig bis",
        id: "lastDate",
        accessorFn: (rowData) =>
          backendDateOrDateTimeToLuxonDateTimeOrNull(rowData.lastDate),
        Cell: ({ cell }) =>
          cell.getValue<DateTime | null>()?.toFormat(DATE_DISPLAY_FORMAT) ??
          "/",
        minSize: 150,
        enableEditing: (row) => row.getValue("lastDate") !== null,
        Edit: ({ cell, row }) => {
          const date = cell.getValue<DateTime | null>();
          return (
            <DateInput
              id={`${row.index.toString()}-lastDate`}
              value={date}
              onChange={(value) => {
                handleChangeUserInput(
                  row.index,
                  null,
                  null,
                  null,
                  luxonDateTimeToBackendDateOrDateTimeOrNull(value)
                );
              }}
            />
          );
        }
      },
      ...consumers.map(
        (consumer) =>
          ({
            header: consumer.consumerName,
            id: consumer.consumer.toString(),
            accessorFn: (rowData) => rowData[consumer.consumer] ?? 0,
            Cell: ({ cell }) => `${cell.getValue()}%`,
            minSize: 150,
            Edit: ({ cell, row }) => (
              <NumberInput
                decimalScale={2}
                fixedDecimalScale
                suffix="%"
                value={parseFloat(cell.getValue<string>())}
                onBlur={(event) => {
                  handleChangeUserInput(
                    row.index,
                    consumer.consumer,
                    event.target.value
                      .replaceAll(".", "")
                      .replace(",", ".")
                      .replace("%", "")
                  );
                }}
              />
            )
          }) satisfies MRT_ColumnDef<ConsumptionShareData>
      )
    ],
    [consumers, handleChangeUserInput]
  );

  const mrtTable = useMantineReactTable<ConsumptionShareData>({
    ...getDefaultMRTOptions({ hideBorder: true }),
    ...getDefaultMRTRowEditModeOptions({
      rowActions: {
        edit: {
          text: "Bearbeiten"
        },
        delete: {
          text: "Löschen",
          enableDelete: () => consumptionShareData.length > 1,
          onClick: (row) => onClickDeleteRow(row.index)
        }
      }
    }),
    onEditingRowSave: ({ exitEditingMode }) => {
      exitEditingMode();
    },
    enableToolbarInternalActions: false,
    enableSorting: false,
    columns: mrtColumns,
    data: consumptionShareData
  });

  return (
    <div className="ConsumptionShareTable">
      <Title fw={500} mb="lg" order={6}>
        Verbrauchsanteil-Übersicht
      </Title>
      <MantineReactTable table={mrtTable} />
    </div>
  );
}

export { ConsumptionShareTable, ConsumptionShareTableProps };
